import {z} from 'zod'

import {tzDateFromIso} from '../../api/zod-helpers'

const rentalAssetTypeRawSchema = z.object({
    id: z.string(),
    name: z.string(),
    default_hourly_rate: z.string().nullable(),
    is_uniquely_identified: z.boolean(),
    archived_at: z.string().nullable().default(null),
    has_unit_numbers: z.boolean().optional(),
    is_driven_truck: z.boolean().optional(),
    default_service_type_id: z.string().nullable().default(null),
    default_specific_type: z.string().nullable().default(null),
})

export const rentalAssetTypeSchema = rentalAssetTypeRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    defaultHourlyRate: raw.default_hourly_rate == null ? null : Number(raw.default_hourly_rate),
    isUniquelyIdentified: raw.is_uniquely_identified,
    hasUnitNumbers: raw.has_unit_numbers ?? true,
    isDrivenTruck: raw.is_driven_truck ?? false,
    defaultServiceTypeId: raw.default_service_type_id,
    defaultSpecificType: raw.default_specific_type,
    archivedAt: raw.archived_at == null ? null : tzDateFromIso.parse(raw.archived_at),
}))

export type RentalAssetTypeRaw = z.input<typeof rentalAssetTypeRawSchema>
export type RentalAssetType = z.infer<typeof rentalAssetTypeSchema>

export const createRentalAssetTypeInputSchema = z.object({
    name: z.string().transform((value) => value.trim()),
    isUniquelyIdentified: z.boolean().optional().default(true),
    hasUnitNumbers: z.boolean().optional().default(true),
})

export type CreateRentalAssetTypeInput = z.input<typeof createRentalAssetTypeInputSchema>

export const createRentalAssetTypeRequestSchema = createRentalAssetTypeInputSchema.transform(
    ({name, isUniquelyIdentified, hasUnitNumbers}) => ({
        name,
        is_uniquely_identified: isUniquelyIdentified,
        has_unit_numbers: hasUnitNumbers,
    }),
)

export type CreateRentalAssetTypeRequestRaw = z.infer<typeof createRentalAssetTypeRequestSchema>

export const updateRentalAssetTypeInputSchema = z.object({
    name: z.string().transform((value) => value.trim()),
    hasUnitNumbers: z.boolean().optional(),
    /** Null clears the default category. Omitted keeps it. */
    defaultWorkCategory: z
        .object({serviceTypeId: z.string().nullable(), specificType: z.string().nullable()})
        .optional(),
})

export type UpdateRentalAssetTypeInput = z.input<typeof updateRentalAssetTypeInputSchema>

export const updateRentalAssetTypeRequestSchema = updateRentalAssetTypeInputSchema.transform(
    ({name, hasUnitNumbers, defaultWorkCategory}) => ({
        name,
        ...(hasUnitNumbers === undefined ? {} : {has_unit_numbers: hasUnitNumbers}),
        ...(defaultWorkCategory === undefined
            ? {}
            : {
                  default_service_type_id: defaultWorkCategory.serviceTypeId,
                  default_specific_type: defaultWorkCategory.specificType,
              }),
    }),
)

export type UpdateRentalAssetTypeRequestRaw = z.infer<typeof updateRentalAssetTypeRequestSchema>

const rentalAssetRawSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    is_uniquely_identified: z.boolean(),
    archived_at: z.string().nullable().default(null),
    dispatch_yard_id: z.string().nullable().optional(),
})

export const rentalAssetSchema = rentalAssetRawSchema.transform((raw) => ({
    id: raw.id,
    name: raw.name,
    rentalAssetTypeId: raw.rental_asset_type_id,
    rentalAssetTypeName: raw.rental_asset_type_name,
    isUniquelyIdentified: raw.is_uniquely_identified,
    archivedAt: raw.archived_at == null ? null : tzDateFromIso.parse(raw.archived_at),
    dispatchYardId: raw.dispatch_yard_id ?? null,
}))

export type RentalAssetRaw = z.infer<typeof rentalAssetRawSchema>
export const identifiedRentalAssetSchema = rentalAssetSchema.refine(
    (asset) => asset.isUniquelyIdentified,
    'Stock equipment must be loaded from quantity summaries',
)

export type RentalAsset = z.infer<typeof rentalAssetSchema>

export const createRentalAssetInputSchema = z.object({
    name: z.string().transform((value) => value.trim()),
    rentalAssetTypeId: z.string(),
})

export type CreateRentalAssetInput = z.input<typeof createRentalAssetInputSchema>

export const createRentalAssetRequestSchema = createRentalAssetInputSchema.transform(
    ({name, rentalAssetTypeId}) => ({
        name,
        rental_asset_type_id: rentalAssetTypeId,
    }),
)

export type CreateRentalAssetRequestRaw = z.infer<typeof createRentalAssetRequestSchema>

export const updateRentalAssetInputSchema = createRentalAssetInputSchema

export type UpdateRentalAssetInput = z.input<typeof updateRentalAssetInputSchema>

export const updateRentalAssetRequestSchema = updateRentalAssetInputSchema.transform(
    ({name, rentalAssetTypeId}) => ({
        name,
        rental_asset_type_id: rentalAssetTypeId,
    }),
)

export type UpdateRentalAssetRequestRaw = z.infer<typeof updateRentalAssetRequestSchema>

const rentalAssetAssignmentRawSchema = z.object({
    id: z.string(),
    job_id: z.string(),
    job_name: z.string(),
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
})

export const rentalAssetAssignmentSchema = rentalAssetAssignmentRawSchema.transform((raw) => ({
    id: raw.id,
    jobId: raw.job_id,
    jobName: raw.job_name,
    startDate: raw.start_date,
    startTime: raw.start_time,
    endDate: raw.end_date,
    endTime: raw.end_time,
}))

export type RentalAssetAssignmentRaw = z.infer<typeof rentalAssetAssignmentRawSchema>
export type RentalAssetAssignment = z.infer<typeof rentalAssetAssignmentSchema>

export const rentalAssetAssignmentListRawSchema = z.object({
    assignments: z.array(rentalAssetAssignmentRawSchema),
})

export const rentalAssetAssignmentListSchema = rentalAssetAssignmentListRawSchema.transform(
    (raw) => ({
        assignments: raw.assignments.map((assignment) =>
            rentalAssetAssignmentSchema.parse(assignment),
        ),
    }),
)

export type RentalAssetAssignmentListRaw = z.infer<typeof rentalAssetAssignmentListRawSchema>
export type RentalAssetAssignmentList = z.infer<typeof rentalAssetAssignmentListSchema>

export type ListRentalAssetAssignmentsOptions = {
    dateFrom: string
    dateTo: string
}

export type ListRentalAssetTypesOptions = {
    includeArchived?: boolean
}

export type ListRentalAssetsOptions = {
    rentalAssetTypeId?: string
    includeArchived?: boolean
}

const thirdPartyRentalCompanyRawSchema = z.object({
    id: z.string(),
    name: z.string(),
})

export const thirdPartyRentalCompanySchema = thirdPartyRentalCompanyRawSchema

export type ThirdPartyRentalCompanyRaw = z.infer<typeof thirdPartyRentalCompanyRawSchema>
export type ThirdPartyRentalCompany = z.infer<typeof thirdPartyRentalCompanySchema>

export const createThirdPartyRentalCompanyInputSchema = z.object({
    name: z.string().transform((value) => value.trim()),
})

export type CreateThirdPartyRentalCompanyInput = z.input<
    typeof createThirdPartyRentalCompanyInputSchema
>

export const createThirdPartyRentalCompanyRequestSchema = createThirdPartyRentalCompanyInputSchema

export type CreateThirdPartyRentalCompanyRequestRaw = z.infer<
    typeof createThirdPartyRentalCompanyRequestSchema
>

export const updateThirdPartyRentalCompanyInputSchema = createThirdPartyRentalCompanyInputSchema

export type UpdateThirdPartyRentalCompanyInput = z.input<
    typeof updateThirdPartyRentalCompanyInputSchema
>

export const updateThirdPartyRentalCompanyRequestSchema = updateThirdPartyRentalCompanyInputSchema

export type UpdateThirdPartyRentalCompanyRequestRaw = z.infer<
    typeof updateThirdPartyRentalCompanyRequestSchema
>

const thirdPartyRentalAssetOfferingRawSchema = z.object({
    id: z.string(),
    company_id: z.string(),
    company_name: z.string(),
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    default_daily_rate: z.string().nullable(),
    archived_at: z.string().nullable(),
})

export const thirdPartyRentalAssetOfferingSchema = thirdPartyRentalAssetOfferingRawSchema.transform(
    (raw) => ({
        id: raw.id,
        companyId: raw.company_id,
        companyName: raw.company_name,
        rentalAssetTypeId: raw.rental_asset_type_id,
        rentalAssetTypeName: raw.rental_asset_type_name,
        defaultDailyRate: raw.default_daily_rate == null ? null : Number(raw.default_daily_rate),
        archivedAt: raw.archived_at == null ? null : tzDateFromIso.parse(raw.archived_at),
    }),
)

export type ThirdPartyRentalAssetOfferingRaw = z.infer<
    typeof thirdPartyRentalAssetOfferingRawSchema
>
export type ThirdPartyRentalAssetOffering = z.infer<typeof thirdPartyRentalAssetOfferingSchema>

export type ListThirdPartyRentalAssetOfferingsOptions = {
    includeArchived?: boolean
    rentalAssetTypeId?: string
    companyId?: string
}

export const createThirdPartyRentalAssetOfferingInputSchema = z.object({
    companyId: z.string(),
    rentalAssetTypeId: z.string(),
    defaultDailyRate: z.number().nullable().optional(),
})

export type CreateThirdPartyRentalAssetOfferingInput = z.input<
    typeof createThirdPartyRentalAssetOfferingInputSchema
>

export const createThirdPartyRentalAssetOfferingRequestSchema =
    createThirdPartyRentalAssetOfferingInputSchema.transform(
        ({companyId, rentalAssetTypeId, defaultDailyRate}) => ({
            company_id: companyId,
            rental_asset_type_id: rentalAssetTypeId,
            default_daily_rate: defaultDailyRate ?? null,
        }),
    )

export type CreateThirdPartyRentalAssetOfferingRequestRaw = z.infer<
    typeof createThirdPartyRentalAssetOfferingRequestSchema
>

export const updateThirdPartyRentalAssetOfferingInputSchema = z.object({
    defaultDailyRate: z.number().nullable(),
})

export type UpdateThirdPartyRentalAssetOfferingInput = z.input<
    typeof updateThirdPartyRentalAssetOfferingInputSchema
>

export const updateThirdPartyRentalAssetOfferingRequestSchema =
    updateThirdPartyRentalAssetOfferingInputSchema.transform(({defaultDailyRate}) => ({
        default_daily_rate: defaultDailyRate,
    }))

export type UpdateThirdPartyRentalAssetOfferingRequestRaw = z.infer<
    typeof updateThirdPartyRentalAssetOfferingRequestSchema
>

const equipmentStockSummaryListItemRawSchema = z.object({
    rental_asset_type_id: z.string(),
    type_name: z.string(),
    total_count: z.number(),
})

export const equipmentStockSummaryListItemSchema = equipmentStockSummaryListItemRawSchema.transform(
    (raw) => ({
        rentalAssetTypeId: raw.rental_asset_type_id,
        typeName: raw.type_name,
        totalCount: raw.total_count,
    }),
)

export type EquipmentStockSummaryListItemRaw = z.infer<
    typeof equipmentStockSummaryListItemRawSchema
>
export type EquipmentStockSummaryListItem = z.infer<typeof equipmentStockSummaryListItemSchema>

const equipmentStockSummariesListRawSchema = z.object({
    summaries: z.array(equipmentStockSummaryListItemRawSchema),
})

export const equipmentStockSummariesListSchema = equipmentStockSummariesListRawSchema.transform(
    (raw) => ({
        summaries: raw.summaries.map((summary) =>
            equipmentStockSummaryListItemSchema.parse(summary),
        ),
    }),
)

export type EquipmentStockSummariesListRaw = z.infer<typeof equipmentStockSummariesListRawSchema>
export type EquipmentStockSummariesList = z.infer<typeof equipmentStockSummariesListSchema>

const stockStatusCountsRawSchema = z.object({
    deployed: z.number(),
    in_transit: z.number(),
    not_deployed: z.number(),
})

export const stockStatusCountsSchema = stockStatusCountsRawSchema.transform((raw) => ({
    deployed: raw.deployed,
    inTransit: raw.in_transit,
    notDeployed: raw.not_deployed,
}))

export type StockStatusCountsRaw = z.infer<typeof stockStatusCountsRawSchema>
export type StockStatusCounts = z.infer<typeof stockStatusCountsSchema>

const deployedByLocationRawSchema = z.object({
    project_id: z.string(),
    project_name: z.string(),
    location_label: z.string(),
    count: z.number(),
})

export const deployedByLocationSchema = deployedByLocationRawSchema.transform((raw) => ({
    projectId: raw.project_id,
    projectName: raw.project_name,
    locationLabel: raw.location_label,
    count: raw.count,
}))

export type DeployedByLocationRaw = z.infer<typeof deployedByLocationRawSchema>
export type DeployedByLocation = z.infer<typeof deployedByLocationSchema>

const availableByYardRawSchema = z.object({
    yard_id: z.string(),
    yard_name: z.string(),
    active: z.boolean(),
    count: z.number(),
})

export const availableByYardSchema = availableByYardRawSchema.transform((raw) => ({
    yardId: raw.yard_id,
    yardName: raw.yard_name,
    active: raw.active,
    count: raw.count,
}))

export type AvailableByYardRaw = z.infer<typeof availableByYardRawSchema>
export type AvailableByYard = z.infer<typeof availableByYardSchema>

const commitmentDayRawSchema = z.object({
    date: z.string(),
    on_site_count: z.number(),
    awaiting_deployment_count: z.number(),
    committed_count: z.number(),
    free_count: z.number(),
    over_capacity_count: z.number(),
})

export const commitmentDaySchema = commitmentDayRawSchema.transform((raw) => ({
    date: raw.date,
    onSiteCount: raw.on_site_count,
    awaitingDeploymentCount: raw.awaiting_deployment_count,
    committedCount: raw.committed_count,
    freeCount: raw.free_count,
    overCapacityCount: raw.over_capacity_count,
}))

export type CommitmentDayRaw = z.infer<typeof commitmentDayRawSchema>
export type CommitmentDay = z.infer<typeof commitmentDaySchema>

const jobCommitmentRawSchema = z.object({
    job_id: z.string(),
    job_name: z.string(),
    project_name: z.string(),
    start_date: z.string(),
    end_date: z.string().nullable(),
    assigned_count: z.number(),
    on_site_count: z.number(),
    awaiting_deployment_count: z.number(),
    returned_count: z.number(),
    location_labels: z.array(z.string()).default([]),
})

export const jobCommitmentSchema = jobCommitmentRawSchema.transform((raw) => ({
    jobId: raw.job_id,
    jobName: raw.job_name,
    projectName: raw.project_name,
    startDate: raw.start_date,
    endDate: raw.end_date,
    assignedCount: raw.assigned_count,
    onSiteCount: raw.on_site_count,
    awaitingDeploymentCount: raw.awaiting_deployment_count,
    returnedCount: raw.returned_count,
    locationLabels: raw.location_labels,
}))

export type JobCommitmentRaw = z.infer<typeof jobCommitmentRawSchema>
export type JobCommitment = z.infer<typeof jobCommitmentSchema>

const equipmentStockSummaryRawSchema = z.object({
    rental_asset_type_id: z.string(),
    type_name: z.string(),
    total_count: z.number(),
    status_counts: stockStatusCountsRawSchema,
    deployed_by_location: z.array(deployedByLocationRawSchema),
    available_by_yard: z.array(availableByYardRawSchema).default([]),
    unknown_yard_count: z.number().default(0),
    commitment_timeline: z.array(commitmentDayRawSchema).default([]),
    job_commitments: z.array(jobCommitmentRawSchema).default([]),
})

export const equipmentStockSummarySchema = equipmentStockSummaryRawSchema.transform((raw) => ({
    rentalAssetTypeId: raw.rental_asset_type_id,
    typeName: raw.type_name,
    totalCount: raw.total_count,
    statusCounts: stockStatusCountsSchema.parse(raw.status_counts),
    deployedByLocation: raw.deployed_by_location.map((location) =>
        deployedByLocationSchema.parse(location),
    ),
    availableByYard: raw.available_by_yard.map((yard) => availableByYardSchema.parse(yard)),
    unknownYardCount: raw.unknown_yard_count,
    commitmentTimeline: raw.commitment_timeline.map((day) => commitmentDaySchema.parse(day)),
    jobCommitments: raw.job_commitments.map((commitment) => jobCommitmentSchema.parse(commitment)),
}))

export type EquipmentStockSummaryRaw = z.infer<typeof equipmentStockSummaryRawSchema>
export type EquipmentStockSummary = z.infer<typeof equipmentStockSummarySchema>

const stockAdjustmentBlockedReasonRawSchema = z.object({
    reason: z.enum(['deployed_or_in_transit', 'assigned_to_job']),
    count: z.number(),
})

export const stockAdjustmentBlockedReasonSchema = stockAdjustmentBlockedReasonRawSchema.transform(
    (raw) => ({
        reason: raw.reason,
        count: raw.count,
    }),
)

export type StockAdjustmentBlockedReasonRaw = z.infer<typeof stockAdjustmentBlockedReasonRawSchema>
export type StockAdjustmentBlockedReason = z.infer<typeof stockAdjustmentBlockedReasonSchema>

export const stockLevelAdjustmentRequestSchema = z.object({
    delta: z.number().int(),
})

export type StockLevelAdjustmentInput = z.infer<typeof stockLevelAdjustmentRequestSchema>

const stockLevelAdjustmentResponseRawSchema = z.object({
    previous_count: z.number(),
    new_count: z.number(),
    requested_delta: z.number(),
    applied_delta: z.number(),
    added: z.number(),
    removed: z.number(),
    blocked_count: z.number(),
    blocked_reasons: z.array(stockAdjustmentBlockedReasonRawSchema),
})

export const stockLevelAdjustmentResponseSchema = stockLevelAdjustmentResponseRawSchema.transform(
    (raw) => ({
        previousCount: raw.previous_count,
        newCount: raw.new_count,
        requestedDelta: raw.requested_delta,
        appliedDelta: raw.applied_delta,
        added: raw.added,
        removed: raw.removed,
        blockedCount: raw.blocked_count,
        blockedReasons: raw.blocked_reasons.map((reason) =>
            stockAdjustmentBlockedReasonSchema.parse(reason),
        ),
    }),
)

export type StockLevelAdjustmentResponseRaw = z.infer<typeof stockLevelAdjustmentResponseRawSchema>
export type StockLevelAdjustmentResponse = z.infer<typeof stockLevelAdjustmentResponseSchema>

const fleetAssignmentWindowRawSchema = z.object({
    date_from: z.string(),
    date_to: z.string(),
})

const fleetAssignmentRawSchema = z.object({
    id: z.string(),
    job_id: z.string(),
    job_name: z.string(),
    start_date: z.string(),
    start_time: z.string().nullable(),
    end_date: z.string().nullable(),
    end_time: z.string().nullable(),
})

const fleetDeploymentStatusRawSchema = z.object({
    status: z.enum(['available', 'deployed', 'picked_up', 'dispatched']),
    timestamp: z.string().nullable(),
    location_label: z.string().nullable(),
})

const fleetUnitRawSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    deployment_status: fleetDeploymentStatusRawSchema,
    upcoming_assignments: z.array(fleetAssignmentRawSchema),
})

const equipmentTypeFleetRawSchema = z.object({
    rental_asset_type_id: z.string(),
    type_name: z.string(),
    assignment_window: fleetAssignmentWindowRawSchema,
    units: z.array(fleetUnitRawSchema),
})

export const equipmentTypeFleetSchema = equipmentTypeFleetRawSchema.transform((raw) => ({
    rentalAssetTypeId: raw.rental_asset_type_id,
    typeName: raw.type_name,
    assignmentWindow: {
        dateFrom: raw.assignment_window.date_from,
        dateTo: raw.assignment_window.date_to,
    },
    units: raw.units.map((unit) => ({
        id: unit.id,
        name: unit.name,
        deploymentStatus: {
            status: unit.deployment_status.status,
            timestamp: unit.deployment_status.timestamp,
            locationLabel: unit.deployment_status.location_label,
        },
        upcomingAssignments: unit.upcoming_assignments.map((assignment) => ({
            id: assignment.id,
            jobId: assignment.job_id,
            jobName: assignment.job_name,
            startDate: assignment.start_date,
            startTime: assignment.start_time,
            endDate: assignment.end_date,
            endTime: assignment.end_time,
        })),
    })),
}))

export type EquipmentTypeFleetRaw = z.infer<typeof equipmentTypeFleetRawSchema>
export type EquipmentTypeFleet = z.infer<typeof equipmentTypeFleetSchema>
export type FleetUnit = EquipmentTypeFleet['units'][number]
export type FleetAssignment = FleetUnit['upcomingAssignments'][number]
export type FleetDeploymentStatus = FleetUnit['deploymentStatus']

const rentalAssetImportPreviewRowRawSchema = z.object({
    row_index: z.number(),
    name: z.string(),
    rental_asset_type: z.string(),
    status: z.enum(['valid', 'invalid']),
    error: z.string().nullable().default(null),
})

export const rentalAssetImportPreviewRowSchema = rentalAssetImportPreviewRowRawSchema.transform(
    (raw) => ({
        rowIndex: raw.row_index,
        name: raw.name,
        rentalAssetType: raw.rental_asset_type,
        status: raw.status,
        error: raw.error,
    }),
)

export type RentalAssetImportPreviewRowRaw = z.infer<typeof rentalAssetImportPreviewRowRawSchema>
export type RentalAssetImportPreviewRow = z.infer<typeof rentalAssetImportPreviewRowSchema>

const rentalAssetImportPreviewRawSchema = z.object({
    current_template_version: z.string(),
    template_outdated: z.boolean(),
    warning: z.string().nullable().default(null),
    rows: z.array(rentalAssetImportPreviewRowRawSchema),
})

export const rentalAssetImportPreviewSchema = rentalAssetImportPreviewRawSchema.transform(
    (raw) => ({
        currentTemplateVersion: raw.current_template_version,
        templateOutdated: raw.template_outdated,
        warning: raw.warning,
        rows: raw.rows.map((row) => rentalAssetImportPreviewRowSchema.parse(row)),
    }),
)

export type RentalAssetImportPreviewRaw = z.infer<typeof rentalAssetImportPreviewRawSchema>
export type RentalAssetImportPreview = z.infer<typeof rentalAssetImportPreviewSchema>

const rentalAssetImportErrorRawSchema = z.object({
    row_index: z.number(),
    message: z.string(),
})

export const rentalAssetImportErrorSchema = rentalAssetImportErrorRawSchema.transform((raw) => ({
    rowIndex: raw.row_index,
    message: raw.message,
}))

export type RentalAssetImportErrorRaw = z.infer<typeof rentalAssetImportErrorRawSchema>
export type RentalAssetImportError = z.infer<typeof rentalAssetImportErrorSchema>

const rentalAssetImportResultRawSchema = z.object({
    imported_count: z.number(),
    skipped_count: z.number(),
    current_template_version: z.string(),
    template_outdated: z.boolean(),
    warning: z.string().nullable().default(null),
    errors: z.array(rentalAssetImportErrorRawSchema),
})

export const rentalAssetImportResultSchema = rentalAssetImportResultRawSchema.transform((raw) => ({
    importedCount: raw.imported_count,
    skippedCount: raw.skipped_count,
    currentTemplateVersion: raw.current_template_version,
    templateOutdated: raw.template_outdated,
    warning: raw.warning,
    errors: raw.errors.map((error) => rentalAssetImportErrorSchema.parse(error)),
}))

export type RentalAssetImportResultRaw = z.infer<typeof rentalAssetImportResultRawSchema>
export type RentalAssetImportResult = z.infer<typeof rentalAssetImportResultSchema>
